import { capitalize, filter, snakeCase } from "lodash";
import { Link } from "react-router-dom";
import { ReportButton } from "../../../components/Button";
import { MarkText } from "../../../components/MarkText";
import { CONTRACT_STATUS, GRANTS, GRANT_STATUS, ORG_STATUS, PACKAGE_STATUS, SYSARCH, USER_STATUS } from "../../../settings";
import { getTitle, highlightText, replaceBrackets } from "../../../utils";
import { CircleIcon } from "../../../icons/CircleIcon";
import { Badge } from "../../../components/Circle";

export const audit = ({ isAdmin, auditRead, pathname, search, context, showForm, tab }) => {

    const tabs = {
        downloads: {
            enabled: isAdmin,
            header: isAdmin && <ReportButton makeReport={() => showForm("download_audit_report")} />,
            title: "Packages Download Audit",
            columns: [
                {
                    title: "Download date",
                    dataIndex: "downloadDatetime",
                    defaultSortOrder: "desc",
                    width: 180,
                    sorter: (a, b) => { if (a && a.hasOwnProperty("downloadDatetime") && a.downloadDatetime) return a.downloadDatetime.localeCompare(b.downloadDatetime) }
                },
                {
                    title: "Package Name / File Name",
                    dataIndex: "packageName",
                    render: (text, record, index) => (
                        <Link to={{ pathname: `${pathname}/${record.auditNumber}`, state: { from: pathname + search }}}>
                            <span title={text + " / " + record.saveAs}>
                                <MarkText text={highlightText(text, context) + " / " + highlightText(record.saveAs, context)} />
                            </span>
                        </Link>
                    ),
                    sorter: (a, b) => { if (a && a.hasOwnProperty("name") && a.name) return a.name.localeCompare(b.name) }
                },
                {
                    title: "Version",
                    dataIndex: "version",
                    width: 100,
                    render: (text, record, index) => (
                        <MarkText text={highlightText(text, context)} />
                    ),
                    sorter: (a, b) => { if (a && a.hasOwnProperty("version") && a.version) return a.version.localeCompare(b.version) }
                },
                {
                    title: "Arch",
                    dataIndex: 'sysarch',
                    width: 150,
                    render: (text, record, index) => (
                        <MarkText text={highlightText(text, context)} />
                    ),
                    sorter: (a, b) => { if (a && a.hasOwnProperty("sysarch") && a.sysarch) return toString(a.sysarch).localeCompare(toString(b.sysarch)) }
                },
                {
                    title: "User",
                    dataIndex: "fullName",
                    render: (text, record, index) => (
                        <Link to={{ pathname: `/users/${record.email}`, state: { from: pathname + search }}}>
                            <MarkText text={highlightText(replaceBrackets(text), context)} />
                        </Link>
                    ),
                    sorter: (a, b) => { if (a && a.hasOwnProperty("email") && a.email) return a.email.localeCompare(b.email) }
                },
                {
                    title: "Organization",
                    dataIndex: "orgName",
                    render: (text, record, index) => (
                        <MarkText nowrap title={text} text={highlightText(replaceBrackets(text), context)} />
                    ),
                    sorter: (a, b) => { if (a && a.hasOwnProperty("org") && a.org) return a.org.localeCompare(b.org) }
                }
            ],
            searchOptions: [
                { key: "sysarch", title: "System Architecture", type: "select", options: SYSARCH, placeholder: "System architecture that the package is compatible with.", multiple: true },
            ]
        },
        users: {
            enabled: auditRead,
            header: isAdmin && <ReportButton makeReport={() => showForm("users_audit_report")} />,
            title: "Users audit",
            columns: [
                {
                    title: "Activity datetime",
                    dataIndex: "updateDatetime",
                    width: 200,
                    defaultSortOrder: "desc",
                    sorter: (a, b) => { if (a && a.hasOwnProperty("updateDatetime")) return a.updateDatetime.localeCompare(b.updateDatetime) },
                },
                {
                    title: "E-Mail",
                    dataIndex: "email",
                    render: (text, record, index) => (<Link to={{ pathname: `${pathname}/${record.auditNumber}`, state: { from: pathname + search }}}>
                        <MarkText text={highlightText(text, context)} />
                    </Link>),
                    sorter: (a, b) => { if (a && a.hasOwnProperty("email") && a.email) return a.email.localeCompare(b.email) }
                },
                {
                    title: "User Name",
                    dataIndex: "fullName",
                    render: (text, record, index) => <MarkText text={highlightText(text, context)} />,
                    sorter: (a, b) => { if (a && a.hasOwnProperty("fullName")) return a.fullName.localeCompare(b.fullName) },
                },
                isAdmin ? {
                    title: "Organization",
                    dataIndex: "orgName",
                    render: (text, record, index) => (
                        <Link to={`/organisations/${record.orgAlias}`}>
                            <MarkText nowrap title={text} text={highlightText(replaceBrackets(text), context)} />
                        </Link>
                    ),
                    sorter: (a, b) => { if (a && a.hasOwnProperty("orgName") && a.orgName) return a.orgName.localeCompare(b.orgName) }
                } : {},
                {
                    title: "Action",
                    dataIndex: "userAction",
                    width: 150,
                    render: (text) => <MarkText text={highlightText(capitalize(getTitle(snakeCase(text), USER_STATUS)), context)} title={capitalize(getTitle(text, USER_STATUS))} />,
                    sorter: (a, b) => { if (a && a.hasOwnProperty("userAction")) return a.userAction.localeCompare(b.userAction) },
                },
                {
                    title: "Updated By",
                    dataIndex: "updatedBy",
                    render: (text, record, index) => <MarkText title={text} text={highlightText(text, context)} />,
                    sorter: (a, b) => { if (a && a.hasOwnProperty("updatedBy")) return a.updatedBy.localeCompare(b.updatedBy) },
                }
            ],
            searchOptions: [
                { key: "action", title: "Action", type: "select", options: USER_STATUS, placeholder: "User update status.", multiple: true },
            ]
        },
        grants: {
            enabled: auditRead,
            header: isAdmin && <ReportButton makeReport={() => showForm("grants_audit_report")} />,
            title: "User grants audit",
            columns: [
                {
                    title: "Activity datetime",
                    dataIndex: "updateDatetime",
                    width: 200,
                    defaultSortOrder: "desc",
                    sorter: (a, b) => { if (a && a.hasOwnProperty("updateDatetime")) return a.updateDatetime.localeCompare(b.updateDatetime) },
                },
                {
                    title: "E-Mail",
                    dataIndex: "email",
                    render: (text, record, index) => (<Link to={{ pathname: `${pathname}/${record.auditNumber}`, state: { from: pathname + search }}}>
                        <MarkText text={highlightText(text, context)} />
                    </Link>),
                    sorter: (a, b) => { if (a && a.hasOwnProperty("email") && a.email) return a.email.localeCompare(b.email) }
                },
                {
                    title: "User Name",
                    dataIndex: "fullName",
                    render: (text, record, index) => <MarkText text={highlightText(text, context)} />,
                    sorter: (a, b) => { if (a && a.hasOwnProperty("fullName")) return a.fullName.localeCompare(b.fullName) },
                },
                {
                    title: "Grant",
                    dataIndex: "grantName",
                    width: 150,
                    render: (text, record, index) => <MarkText text={highlightText(getTitle(text, GRANTS), context)} />,
                    sorter: (a, b) => { if (a && a.hasOwnProperty("grantName")) return a.grantName.localeCompare(b.grantName) },
                },
                isAdmin ? {
                    title: "Organization",
                    dataIndex: "orgName",
                    render: (text, record, index) => (
                        <MarkText nowrap title={text} text={highlightText(replaceBrackets(text), context)} />
                    ),
                    sorter: (a, b) => { if (a && a.hasOwnProperty("orgName") && a.orgName) return a.orgName.localeCompare(b.orgName) }
                } : {},
                {
                    title: "Status",
                    dataIndex: "status",
                    width: 80,
                    render: (text) => <MarkText nowrap title={text} text={highlightText(text, context)} />,
                    sorter: (a, b) => { if (a && a.hasOwnProperty("action")) return a.action.localeCompare(b.action) },
                },
                {
                    title: "Updated By",
                    dataIndex: "updatedBy",
                    render: (text, record, index) => <MarkText nowrap title={text} text={highlightText(text, context)} />,
                    sorter: (a, b) => { if (a && a.hasOwnProperty("updatedBy")) return a.updatedBy.localeCompare(b.updatedBy) },
                }
            ],
            searchOptions: [
                { key: "grant", title: "Grant", type: "select", options:  isAdmin ? GRANTS : filter(GRANTS, { visible: true }), placeholder: "Grant which the user being issued with or revoked.", multiple: true },
                { key: "status", title: "Status", type: "select", options: GRANT_STATUS, placeholder: "User grant update status.", multiple: true }
            ]
        },
        impersonations: {
            enabled: auditRead,
            header: isAdmin && <ReportButton makeReport={() => showForm("impersonations_audit_report")} />,
            title: "User impersonation audit",
            columns: [
                {
                    title: "Impersonation started",
                    dataIndex: "startDate",
                    width: 200,
                    defaultSortOrder: "desc",
                    sorter: (a, b) => { if (a && a.hasOwnProperty("startDate")) return a.startDate.localeCompare(b.startDate) },
                },
                {
                    title: "Impersonation dropped",
                    dataIndex: "endDate",
                    width: 200,
                    defaultSortOrder: "desc",
                    sorter: (a, b) => { if (a && a.hasOwnProperty("endDate")) return a.endDate.localeCompare(b.endDate) },
                },
                {
                    title: "E-Mail",
                    dataIndex: "email",
                    render: (text, record, index) => (<Link to={{ pathname: `${pathname}/${record.auditNumber}`, state: { from: pathname + search }}}>
                        <MarkText text={highlightText(text, context)} />
                    </Link>),
                    sorter: (a, b) => { if (a && a.hasOwnProperty("email") && a.email) return a.email.localeCompare(b.email) }
                },
                {
                    title: "User Name",
                    dataIndex: "fullName",
                    render: (text, record, index) => <MarkText text={highlightText(text, context)} />,
                    sorter: (a, b) => { if (a && a.hasOwnProperty("fullName")) return a.fullName.localeCompare(b.fullName) },
                },
                isAdmin ? {
                    title: "Organization",
                    dataIndex: "orgImpersonateName",
                    render: (text, record, index) => (
                        <MarkText nowrap title={text} text={highlightText(replaceBrackets(text), context)} />
                    ),
                    sorter: (a, b) => { if (a && a.hasOwnProperty("orgImpersonateName") && a.orgImpersonateName) return a.orgImpersonateName.localeCompare(b.orgImpersonateName) }
                } : {},
            ]
        },
        contracts: {
            enabled: auditRead,
            header: isAdmin && <ReportButton makeReport={() => showForm("orgs_contracts_audit_report")} />,
            title: "Organizations Contracts Audit",
            columns: [
                {
                    title: "Update datetime",
                    dataIndex: "updatedDatetime",
                    width: 200,
                    defaultSortOrder: "desc",
                    sorter: (a, b) => { if (a && a.hasOwnProperty("updatedDatetime")) return a.updatedDatetime.localeCompare(b.updatedDatetime) },
                },
                {
                    title: "Contract Name",
                    dataIndex: 'contractName',
                    width: 180,
                    render: (text, record, index) => (
                        <Link to={{ pathname: `${pathname}/${record.auditNumber}`, state: { from: pathname + search }}}>
                            <MarkText nowrap title={text} text={highlightText(text, context)} />
                        </Link>
                    ),
                    sorter: (a, b) => { if (a && a.hasOwnProperty("contractName") && a.contractName) return a.contractName.localeCompare(b.contractName) }
                },
                isAdmin ? {
                    title: "Organization",
                    dataIndex: 'orgName',
                    render: (text, record, index) => <MarkText nowrap title={text} text={highlightText(replaceBrackets(text), context)} />,
                    sorter: (a, b) => { if (a && a.hasOwnProperty("orgName") && a.orgName) return a.orgName.localeCompare(b.orgName) }
                }
                : {},
                {
                    title: "Status",
                    dataIndex: 'action',
                    render: (text, record, index) => <MarkText nowrap title={text} text={highlightText(capitalize(text), context)} />,
                    sorter: (a, b) => { if (a && a.hasOwnProperty("action") && a.action) return a.action.localeCompare(b.action) }
                },
                {
                    title: "Updated by",
                    dataIndex: 'updatedBy',
                    render: (text, record, index) => <MarkText nowrap title={text} text={highlightText(text, context)} />,
                    sorter: (a, b) => { if (a && a.hasOwnProperty("updatedBy") && a.updatedBy) return a.updatedBy.localeCompare(b.updatedBy) }
                }
            ],
            searchOptions: [
                { key: "status", title: "Status", type: "select", options: CONTRACT_STATUS, placeholder: "Organization contract update status.", multiple: true }
            ]
        },
        packages: {
            enabled: isAdmin,
            header: isAdmin && <ReportButton makeReport={() => showForm("packages_audit_report")} />,
            title: "Packages Audit",
            columns: [
                {
                    title: "Activity datetime",
                    dataIndex: "activityDatetime",
                    width: 200,
                    defaultSortOrder: "desc",
                    sorter: (a, b) => { if (a && a.hasOwnProperty("activityDatetime") && a.activityDatetime) return a.activityDatetime.localeCompare(b.activityDatetime) },
                },
                {
                    title: "Package Name",
                    dataIndex: "packageName",
                    defaultSortOrder: "asc",
                    render: (text, record, index) => (
                        <Link to={{pathname: `${pathname}/${record.auditNumber}`, state: { from: pathname + search }}}>
                            <MarkText style={{ fontWeight: 600 }} text={highlightText(text, context)} />
                        </Link>
                    ),
                    sorter: (a, b) => { if (a && a.hasOwnProperty("packageName") && a.packageName) return a.packageName.localeCompare(b.packageName) }
                },
                {
                    title: "Status",
                    dataIndex: "status",
                    width: 150,
                    render: (text) => getTitle(text, PACKAGE_STATUS),
                    sorter: (a, b) => { if (a && a.hasOwnProperty("status") && a.status) return a.status.localeCompare(b.status) },
                },
                {
                    title: "Done By",
                    dataIndex: "email",
                    render: (text, record, index) => <MarkText title={text} text={highlightText(text, context)} />,
                    sorter: (a, b) => { if (a && a.hasOwnProperty("email") && a.email) return a.email.localeCompare(b.email) },
                }
            ],
            searchOptions: [
                { key: "action", title: "Status", type: "select", options: PACKAGE_STATUS, placeholder: "Package update status.", multiple: true },
            ]
        },
        permissions: {
            enabled: isAdmin,
            // header: isAdmin && <ReportButton makeReport={() => showForm("packages_audit_report")} />,
            title: "Packages Permissions Audit",
            columns: [
                {
                    title: <CircleIcon size={16} />,
                    dataIndex: 'deleted',
                    render: (text, record, index) => <Badge size={8} color={text ? "var(--sp-error-color)" : "var(--sp-success-color)"}/>,
                    width: 56,
                    sorter: (a, b) => { if (a && a.hasOwnProperty("deleted")) return (a.deleted === b.deleted) ? 0 : a.deleted ? -1 : 1 }
                },
                {
                    title: "Activity datetime",
                    dataIndex: "activityDatetime",
                    width: 165,
                    defaultSortOrder: "desc",
                    sorter: (a, b) => { if (a && a.hasOwnProperty("activityDatetime") && a.activityDatetime) return a.activityDatetime.localeCompare(b.activityDatetime) },
                },
                {
                    title: "Package Name",
                    dataIndex: "packageName",
                    defaultSortOrder: "asc",
                    render: (text, record, index) => (
                        <Link to={{pathname: `${pathname}/${record.auditNumber}`, state: { from: pathname + search }}} title={text} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                            <MarkText style={{ fontWeight: 600 }} text={highlightText(text, context)} />
                        </Link>
                    ),
                    sorter: (a, b) => { if (a && a.hasOwnProperty("packageName") && a.packageName) return a.packageName.localeCompare(b.packageName) }
                },
                {
                    title: "Contracts",
                    dataIndex: "contracts",
                    render: (text) => <MarkText title={text} text={highlightText(text.split(",").join(", "), context)} />,
                    sorter: (a, b) => { if (a && a.hasOwnProperty("status") && a.status) return a.status.localeCompare(b.status) },
                },
                {
                    title: "Ecokits",
                    dataIndex: "ecokits",
                    width: 100,
                    sorter: (a, b) => { if (a && a.hasOwnProperty("ecokits") && a.ecokits) return a.ecokits.localeCompare(b.ecokits) }
                },
                {
                    title: "Rights to minor releases",
                    dataIndex: "rToMinorRel",
                    width: 120,
                    sorter: (a, b) => { if (a && a.hasOwnProperty("rToMinorRel") && a.rToMinorRel) return a.rToMinorRel.localeCompare(b.rToMinorRel) }
                },
                {
                    title: "Updated By",
                    dataIndex: "email",
                    render: (text, record, index) => <MarkText title={text} text={highlightText(text, context)} />,
                    sorter: (a, b) => { if (a && a.hasOwnProperty("email") && a.email) return a.email.localeCompare(b.email) },
                }
            ]
        },
        orgs: {
            enabled: isAdmin,
            header: isAdmin && <ReportButton makeReport={() => showForm("orgs_audit_report")} />,
            title: "Organizations Audit",
            columns: [
                {
                    title: "Activity datetime",
                    dataIndex: "activityDatetime",
                    width: 200,
                    defaultSortOrder: "desc",
                    sorter: (a, b) => { if (a && a.hasOwnProperty("activityDatetime") && a.activityDatetime) return a.activityDatetime.localeCompare(b.activityDatetime) },
                },
                {
                    title: "Organization Name",
                    dataIndex: "orgName",
                    render: (text, record, index) => (
                        <Link to={{pathname: `${pathname}/${record.auditNumber}`, state: { from: pathname + search }}}>
                            <MarkText style={{ fontWeight: 600 }} text={highlightText(text, context)} />
                        </Link>
                    ),
                    sorter: (a, b) => { if (a && a.hasOwnProperty("orgName") && a.orgName) return a.orgName.localeCompare(b.orgName) }
                },
                {
                    title: "Alias",
                    dataIndex: "orgAlias",
                    width: 100,
                    render: (text, record, index) => <MarkText title={text} text={highlightText(text, context)} />,
                    sorter: (a, b) => { if (a && a.hasOwnProperty("orgAlias") && a.orgAlias) return a.orgAlias.localeCompare(b.orgAlias) }
                },
                {
                    title: "Status",
                    dataIndex: "status",
                    width: 150,
                    render: (text) => getTitle(text, ORG_STATUS),
                    sorter: (a, b) => { if (a && a.hasOwnProperty("status") && a.status) return a.status.localeCompare(b.status) },
                },
                {
                    title: "Done By",
                    dataIndex: "email",
                    render: (text, record, index) => <MarkText title={text} text={highlightText(text, context)} />,
                    sorter: (a, b) => { if (a && a.hasOwnProperty("email") && a.email) return a.email.localeCompare(b.email) },
                }
            ],
            searchOptions: [
                { key: "status", title: "Status", type: "select", options: ORG_STATUS, placeholder: "Organization update status.", multiple: true },
            ]
        }
    };

    if (Object.keys(tabs).includes(tab)) {
        tabs[tab].breadcrumbs = [
            { title: "Audit", href: "/audit" },
            { title: tabs[tab].title }
        ]

        return tabs[tab];
    }

    return null;
};